/**
 * Utilidades para trabajar con traducciones y contenido multilingüe
 * Diseñado con accesibilidad en mente para usuarios con síndrome de Down
 */

/**
 * Realiza una traducción de manera segura, proporcionando un valor predeterminado
 * si la función de traducción no está disponible o no puede traducir la clave.
 *
 * @param {string} key - Clave de traducción
 * @param {string} defaultValue - Valor predeterminado si la traducción falla
 * @param {Object} options - Opciones de interpolación para la traducción
 * @param {Function} t - Función de traducción (normalmente de react-i18next)
 * @returns {string} - Texto traducido o valor predeterminado
 */
export const safeTranslate = (key, defaultValue, options = {}, t) => {
  if (!t || typeof t !== "function") {
    return defaultValue;
  }

  try {
    const translation = t(key, {
      ...options,
      defaultValue,
    });

    return translation || defaultValue;
  } catch (error) {
    console.error(`Error translating key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Simplifica un texto para hacerlo más accesible para personas con dificultades cognitivas
 *
 * @param {string} text - Texto original a simplificar
 * @param {Object} options - Opciones de simplificación
 * @returns {string} - Texto simplificado
 */
export const simplifyText = (text, options = {}) => {
  if (!text) return "";

  // Nivel de simplificación (bajo, medio, alto)
  const level = options.level || "medium";

  let simplified = text;

  // Reemplazar expresiones complejas con alternativas más simples
  const complexTerms = {
    "sin embargo": "pero",
    "no obstante": "pero",
    "en consecuencia": "por esto",
    "por consiguiente": "por esto",
    "debido a": "por",
    "en relación a": "sobre",
    "en referencia a": "sobre",
    "con respecto a": "sobre",
    "a pesar de": "aunque",
    "en virtud de": "por",
    "en función de": "según",
    "en base a": "según",
    "de conformidad con": "según",
    "en el ámbito de": "en",
    "en el marco de": "en",
  };

  // Aplicar simplificaciones según el nivel
  if (level === "high") {
    // Nivel alto: reemplazar todos los términos complejos
    Object.entries(complexTerms).forEach(([complex, simple]) => {
      const regex = new RegExp(complex, "gi");
      simplified = simplified.replace(regex, simple);
    });

    // Acortar oraciones muy largas
    simplified = simplified
      .replace(/([.!?])\s+/g, "$1\n")
      .split("\n")
      .map((sentence) => {
        if (sentence.split(" ").length > 15) {
          // Intentar dividir oraciones largas
          return sentence.replace(/,\s+/g, ".\n");
        }
        return sentence;
      })
      .join("\n");
  } else if (level === "medium") {
    // Nivel medio: reemplazar algunos términos
    Object.entries(complexTerms).forEach(([complex, simple]) => {
      const regex = new RegExp(`\\b${complex}\\b`, "gi");
      simplified = simplified.replace(regex, simple);
    });
  }

  return simplified;
};

/**
 * Convierte texto a voz utilizando la API de síntesis de voz del navegador
 *
 * @param {string} text - Texto a reproducir
 * @param {Object} options - Opciones de reproducción
 * @returns {Promise<void>}
 */
export const speakText = (text, options = {}) => {
  return new Promise((resolve, reject) => {
    if (!window.speechSynthesis) {
      reject(new Error("Speech synthesis not supported"));
      return;
    }

    try {
      // Cancelar cualquier síntesis en curso
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      // Configurar opciones
      if (options.voice) utterance.voice = options.voice;
      if (options.rate) utterance.rate = options.rate;
      if (options.pitch) utterance.pitch = options.pitch;
      if (options.volume) utterance.volume = options.volume;

      // Idioma por defecto: español
      utterance.lang = options.lang || "es-ES";

      // Eventos
      utterance.onend = () => resolve();
      utterance.onerror = (error) => reject(error);

      // Reproducir
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Detecta el idioma de un texto utilizando heurísticas simples
 *
 * @param {string} text - Texto para detectar idioma
 * @returns {string} - Código de idioma (es, en, etc.)
 */
export const detectLanguage = (text) => {
  if (!text || text.length < 5) return "es"; // Por defecto

  // Palabras frecuentes en español
  const spanishWords = [
    "el",
    "la",
    "los",
    "las",
    "un",
    "una",
    "unos",
    "unas",
    "y",
    "o",
    "pero",
    "porque",
    "que",
    "como",
    "cuando",
    "donde",
    "quien",
    "cual",
    "qué",
    "cómo",
    "cuándo",
    "dónde",
    "quién",
    "cuál",
    "para",
    "por",
    "con",
    "sin",
    "sobre",
    "bajo",
    "desde",
    "hasta",
    "durante",
    "mediante",
    "según",
    "contra",
    "hacia",
  ];

  // Palabras frecuentes en inglés
  const englishWords = [
    "the",
    "a",
    "an",
    "and",
    "or",
    "but",
    "because",
    "that",
    "like",
    "when",
    "where",
    "who",
    "which",
    "what",
    "how",
    "for",
    "to",
    "in",
    "on",
    "at",
    "by",
    "with",
    "without",
    "about",
    "from",
    "since",
    "until",
    "during",
    "through",
    "before",
    "after",
    "above",
    "below",
    "over",
    "under",
  ];

  // Normalizar texto
  const normalized = text
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "")
    .split(/\s+/);

  // Contar coincidencias
  let spanishCount = 0;
  let englishCount = 0;

  normalized.forEach((word) => {
    if (spanishWords.includes(word)) spanishCount++;
    if (englishWords.includes(word)) englishCount++;
  });

  // Determinar idioma
  if (spanishCount > englishCount) return "es";
  if (englishCount > spanishCount) return "en";

  // Si no hay suficientes coincidencias, buscar caracteres específicos
  if (text.match(/[áéíóúüñ]/i)) return "es";

  return "es"; // Por defecto: español
};
